import { useMemo } from "react";

import { API_UI_FLAGS, useIsApiSdkEnabled } from "domain/synthetics/uiFlags/useIsApiSdkEnabled";
import { ApiDataSource } from "lib/metrics/types";
import { useApiDataFallbackCounter } from "lib/metrics/useApiDataFallbackCounter";
import type { ContractsChainId } from "sdk/configs/chains";
import { composeFullMarketsInfoData, composeRawMarketsInfoData } from "sdk/utils/markets";
import type { MarketsInfoData, RawMarketsInfoData } from "sdk/utils/markets/types";

import type { TokensData } from "../../tokens";
import { useClaimableFundingDataRequest } from "../useClaimableFundingDataRequest";
import { useApiMarketsInfoRequest } from "./useApiMarketsInfoRequest";
import { useRpcMarketsInfoRequest } from "./useRpcMarketsInfoRequest";

export type MarketsInfoResult = {
  marketsInfoData?: MarketsInfoData;
  error?: Error;
  isLoading: boolean;
  dataSource?: ApiDataSource;
};

export function useMarketsInfoRequest(
  chainId: ContractsChainId,
  { tokensData }: { tokensData: TokensData | undefined }
): MarketsInfoResult {
  const { claimableFundingData } = useClaimableFundingDataRequest(chainId);

  const isApiSdkEnabled = useIsApiSdkEnabled(API_UI_FLAGS.markets);

  const {
    marketsInfoData: apiMarketsInfoData,
    isStale: isApiStale,
    error: apiError,
  } = useApiMarketsInfoRequest(chainId, { enabled: isApiSdkEnabled });

  const shouldFallbackToRpc = !isApiSdkEnabled || apiError || isApiStale;

  const { marketsAddresses, fastMarketInfoData, marketsData, marketsValuesData, marketsConfigsData, marketsConstants } =
    useRpcMarketsInfoRequest({
      chainId,
      tokensData,
      enabled: shouldFallbackToRpc,
    });

  const rpcMarketsInfoDataReady = marketsData && marketsValuesData && marketsConfigsData && marketsConstants;

  const rawMarketsInfoData = useMemo((): RawMarketsInfoData | undefined => {
    if (apiMarketsInfoData) {
      return apiMarketsInfoData as RawMarketsInfoData;
    }

    if (marketsAddresses && rpcMarketsInfoDataReady) {
      return composeRawMarketsInfoData({
        marketsAddresses,
        marketsData,
        marketsValuesData,
        marketsConfigsData,
        marketsConstants,
      });
    }

    if (fastMarketInfoData) {
      return fastMarketInfoData;
    }

    return undefined;
  }, [
    apiMarketsInfoData,
    fastMarketInfoData,
    marketsAddresses,
    rpcMarketsInfoDataReady,
    marketsData,
    marketsValuesData,
    marketsConfigsData,
    marketsConstants,
  ]);

  const mergedData = useMemo(() => {
    if (!marketsAddresses || !tokensData || !rawMarketsInfoData) {
      return undefined;
    }

    return composeFullMarketsInfoData({
      chainId,
      marketsAddresses,
      rawMarketsInfoData,
      tokensData,
      claimableFundingData,
    });
  }, [marketsAddresses, tokensData, rawMarketsInfoData, chainId, claimableFundingData]);

  const dataSource: ApiDataSource | undefined = mergedData ? (apiMarketsInfoData ? "api" : "rpc") : undefined;

  useApiDataFallbackCounter({
    domain: "markets",
    chainId,
    apiEnabled: isApiSdkEnabled,
    apiData: apiMarketsInfoData,
    isApiStale,
    apiError,
  });

  return {
    marketsInfoData: mergedData,
    error: apiError,
    isLoading: shouldFallbackToRpc && !rpcMarketsInfoDataReady,
    dataSource,
  };
}
